import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import type { WhatsAppTransportType } from "../../Objects/RespostaAi/WhatsAppTransportType"
import type { ISaveRespostaAiGlobalKeyPayload } from "../../Objects/RespostaAi/ISaveRespostaAiGlobalKeyPayload"
import type { IRespostaAiInstance } from "../../Objects/RespostaAi/IRespostaAiInstance"
import type { IRespostaAiSettings } from "../../Objects/RespostaAi/IRespostaAiSettings"

export const useRespostaAiIntegration = () => {
  const { t } = useTranslation(["pages", "general"])

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const execute = async <T,>(operation: () => Promise<{ success: boolean; message?: string; data?: T }>) => {
    enableLoad()

    try {
      const response = await operation()

      if (!response.success) {
        throw new Error(response.message || t("label_resposta_ai_error_default"))
      }

      return response.data as T
    } catch (error: unknown) {
      setError(
        t("general:label_title_erro"),
        error instanceof Error ? error.message : t("label_resposta_ai_error_default"),
      )

      throw error
    } finally {
      disableLoad()
    }
  }

  const getSettings = () => execute<IRespostaAiSettings>(() => GetRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.SETTINGS))

  const saveGlobalKey = (payload: ISaveRespostaAiGlobalKeyPayload) =>
    execute<IRespostaAiSettings>(() => PutRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.GLOBAL_KEY, payload))

  const listInstances = () =>
    execute<IRespostaAiInstance[]>(() => GetRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.INSTANCES))

  const provision = (instanceId: string) =>
    execute<IRespostaAiSettings>(() => PostRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.PROVISION, { instanceId }))

  const activate = (transport: WhatsAppTransportType) =>
    execute<IRespostaAiSettings>(() => PostRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.ACTIVATE, { transport }))

  const syncKnowledge = () =>
    execute<IRespostaAiSettings>(() => PostRequest(API_ROUTES.RESPOSTA_AI_INTEGRATION.SYNC_KNOWLEDGE, {}))

  return { getSettings, saveGlobalKey, listInstances, provision, activate, syncKnowledge }
}
